import React, { useState } from "react";
import { getAsrAccounting } from "../../services/api";
import DatePickerWithValidation from "../ui/DatePickerWithValidation";
import { toDDMMYYYY } from "../../utils/dateUtils";

const FOP_LABELS: Record<string, string> = {
  CA: "Cash", CC: "Credit Card", CK: "Check", ET: "Exchange Ticket",
  PT: "Pre-Paid", TV: "Travel Voucher", BT: "Travel Bank",
  GR: "Govt Bill", IN: "Involuntary", CT: "Certificate", XX: "Other",
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  open:   { label: "Open",   color: "bg-green-100 text-green-700" },
  closed: { label: "Closed", color: "bg-gray-200 text-gray-600" },
};

function todayISO() { return new Date().toISOString().split("T")[0]; }

function fmtAmt(n: number) {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function safeArray(val: any): any[] {
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
}

function resolveKey(obj: any, ...keys: string[]): any {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined) return obj[k];
  }
  return undefined;
}

function parseAmount(val: any): number {
  if (val == null) return 0;
  if (typeof val === "number") return val;
  const str = typeof val === "string" ? val : (val._ ?? val["#text"] ?? "0");
  return parseFloat(str) || 0;
}

type AccountingEntry = {
  fopCode: string;
  currencyCode: string;
  totalDocuments: number;
  fareAmount: number;
  taxAmount: number;
  totalAmount: number;
};

function extractAccountingEntries(raw: any): AccountingEntry[] {
  const entries = safeArray(
    resolveKey(raw, "AccountingDetail", "ns4:AccountingDetail", "tkt:AccountingDetail",
      "SSFop", "ns4:SSFop", "FopSummary", "ns4:FopSummary")
  );
  return entries.map((e: any) => ({
    fopCode: resolveKey(e, "FopCode", "ns4:FopCode") ?? "",
    currencyCode: resolveKey(e, "CurrencyCode", "ns4:CurrencyCode") ?? "",
    totalDocuments: parseInt(resolveKey(e, "TotalDocuments", "ns4:TotalDocuments") ?? "0", 10) || 0,
    fareAmount: parseAmount(resolveKey(e, "FareAmount", "ns4:FareAmount")),
    taxAmount: parseAmount(resolveKey(e, "TaxAmount", "ns4:TaxAmount")),
    totalAmount: parseAmount(resolveKey(e, "TotalAmount", "ns4:TotalAmount")),
  }));
}

function extractReportStatus(raw: any): string {
  return resolveKey(raw, "ReportStatus", "ns4:ReportStatus", "tkt:ReportStatus", "Status", "ns4:Status") ?? "";
}

function extractRemark(raw: any): string {
  return resolveKey(raw, "Remark", "ns4:Remark", "tkt:Remark") ?? "";
}

const OPERATIONS = [
  { value: "display",     label: "Display" },
  { value: "close",       label: "Close" },
  { value: "setRemark",   label: "Set Remark" },
  { value: "removeRemark",label: "Remove Remark" },
];

const inputCls = "border border-gray-300 rounded-lg px-3 py-2 text-sm text-heading w-full focus:outline-none focus:ring-2 focus:ring-primary";

const AsrAccounting: React.FC = () => {
  const [ticketingProvider, setTicketingProvider] = useState("");
  const [stationNumber, setStationNumber]         = useState("");
  const [employeeNumber, setEmployeeNumber]       = useState("");
  const [reportDate, setReportDate]               = useState(todayISO());
  const [operation, setOperation]                 = useState("display");
  const [remark, setRemark]                       = useState("");

  const [loading, setLoading]       = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [result, setResult]         = useState<any | null>(null);

  const canFetch = ticketingProvider.trim() && stationNumber.trim() && employeeNumber.trim() && reportDate;
  const needsRemark = operation === "setRemark";

  const handleFetch = async () => {
    if (!canFetch) return;
    if (needsRemark && !remark.trim()) return;
    setLoading(true);
    setFetchError(null);
    setResult(null);
    try {
      const data = await getAsrAccounting({
        ticketingProvider: ticketingProvider.trim().toUpperCase(),
        stationNumber: stationNumber.trim(),
        employeeNumber: employeeNumber.trim(),
        reportDate,
        operation,
        ...(remark.trim() ? { remark: remark.trim() } : {}),
      });
      if (!data.success) {
        setFetchError(data.error ?? "Unknown error from server.");
      } else {
        setResult(data);
      }
    } catch (err: any) {
      setFetchError(
        err?.response?.data?.errors?.[0]?.message ??
          err?.response?.data?.error ??
          err?.message ??
          "Failed to execute accounting operation."
      );
    } finally {
      setLoading(false);
    }
  };

  const rawData = result?.rawData ?? {};
  const entries = extractAccountingEntries(rawData);
  const reportStatus = extractReportStatus(rawData);
  const remarkText = extractRemark(rawData);
  const statusCfg = STATUS_LABELS[reportStatus.toLowerCase()] ?? { label: reportStatus || "Unknown", color: "bg-gray-100 text-gray-600" };

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-heading">ASR: Accounting</h1>
        <p className="text-sm text-gray-500 mt-1">
          Agent account management via Sabre TKT_AsrServicesRQ AccountingRQ.
          Requires SabreSonic airline access.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Ticketing Provider *</label>
            <input value={ticketingProvider} onChange={(e) => setTicketingProvider(e.target.value.toUpperCase())}
              placeholder="e.g. 7F" maxLength={2} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Station Number *</label>
            <input value={stationNumber} onChange={(e) => setStationNumber(e.target.value)}
              placeholder="8-digit station" maxLength={10} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Employee Number *</label>
            <input value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)}
              placeholder="6-digit employee" maxLength={10} className={inputCls} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Report Date *</label>
            <DatePickerWithValidation
              selected={reportDate}
              onSelect={setReportDate}
              maxDate={todayISO()}
              placeholder="dd/mm/yyyy"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Operation</label>
            <select value={operation} onChange={(e) => setOperation(e.target.value)} className={inputCls}>
              {OPERATIONS.map((op) => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>
          </div>
          {needsRemark && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Remark *</label>
              <input value={remark} onChange={(e) => setRemark(e.target.value)}
                placeholder="Remark text" className={inputCls} />
            </div>
          )}
        </div>
        <div className="mt-4">
          <button onClick={handleFetch} disabled={loading || !canFetch || (needsRemark && !remark.trim())}
            className="bg-primary text-white font-semibold px-6 py-2 rounded-full hover:bg-blue-800 transition text-sm disabled:opacity-50">
            {loading ? "Loading..." : "Execute"}
          </button>
        </div>
      </div>

      {fetchError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6 text-sm">{fetchError}</div>
      )}

      {result && (
        <>
          {/* Meta */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Operation</span>
              <p className="font-semibold text-heading capitalize">{result.operation}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Report Date</span>
              <p className="font-semibold text-heading">{toDDMMYYYY(result.reportDate)}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs uppercase font-semibold">Employee</span>
              <p className="font-semibold text-heading">{result.employeeNumber}</p>
            </div>
            {reportStatus && (
              <div>
                <span className="text-gray-500 text-xs uppercase font-semibold">Status</span>
                <p><span className={`text-xs font-semibold rounded px-2 py-0.5 ${statusCfg.color}`}>{statusCfg.label}</span></p>
              </div>
            )}
            {remarkText && (
              <div>
                <span className="text-gray-500 text-xs uppercase font-semibold">Remark</span>
                <p className="font-semibold text-heading">{remarkText}</p>
              </div>
            )}
          </div>

          {/* Accounting Entries Table */}
          {entries.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">FOP</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Currency</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Documents</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Fare</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Tax</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="bg-gray-100 text-gray-700 text-xs font-semibold rounded px-2 py-0.5" title={FOP_LABELS[e.fopCode] ?? e.fopCode}>
                            {e.fopCode}
                          </span>
                          {FOP_LABELS[e.fopCode] && (
                            <span className="ml-1 text-gray-400 text-xs hidden lg:inline">{FOP_LABELS[e.fopCode]}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{e.currencyCode}</td>
                        <td className="px-4 py-3 text-right font-mono text-xs whitespace-nowrap">{e.totalDocuments}</td>
                        <td className="px-4 py-3 text-right font-mono text-xs whitespace-nowrap">{fmtAmt(e.fareAmount)}</td>
                        <td className="px-4 py-3 text-right font-mono text-xs whitespace-nowrap">{fmtAmt(e.taxAmount)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-heading whitespace-nowrap">{fmtAmt(e.totalAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-300">
                      <td className="px-4 py-3 text-xs font-bold text-heading uppercase" colSpan={2}>Totals</td>
                      <td className="px-4 py-3 text-right font-bold text-heading text-xs">{entries.reduce((s, e) => s + e.totalDocuments, 0)}</td>
                      <td className="px-4 py-3 text-right font-bold text-heading text-xs">{fmtAmt(entries.reduce((s, e) => s + e.fareAmount, 0))}</td>
                      <td className="px-4 py-3 text-right font-bold text-heading text-xs">{fmtAmt(entries.reduce((s, e) => s + e.taxAmount, 0))}</td>
                      <td className="px-4 py-3 text-right font-bold text-heading text-xs">{fmtAmt(entries.reduce((s, e) => s + e.totalAmount, 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Fallback raw data if no structured entries */}
          {entries.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-xs uppercase font-bold text-gray-500 tracking-widest mb-2">Response Data</h3>
              <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-700 overflow-auto max-h-[500px] whitespace-pre-wrap break-all">
                {JSON.stringify(rawData, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AsrAccounting;
